// The apex — verso-cloud.io with no org in front of it.
//
// A tenant's dashboard lives at <slug>.verso-cloud.io, so the bare domain has no
// org to load and can't guess one: the session is minted against the subdomain,
// and an operator who belongs to several orgs would get whichever the API picked.
// Until now the apex resolved to nothing at all and the browser said
// ERR_NAME_NOT_RESOLVED, which reads as "Verso is down" rather than "you left off
// your organisation".
//
// So: ask, then send them there. Same shape as vista-cloud.io, deliberately —
// it's the same operators on both.
//
// This page is PUBLIC. It renders before Cloudflare Access has anything to say,
// holds no data, and its only output is a redirect to a subdomain that does sit
// behind Access. It must not fetch anything.

import { useState } from 'react';
import { isReservedSlug } from '../lib/orgSlug.js';

const PRODUCT_DOMAIN = import.meta.env.VITE_PRODUCT_DOMAIN || 'verso-cloud.io';

// What a slug may look like. Matches the admin console's rule so the gate can't
// send someone to a hostname the platform would never have created.
const SLUG_RE = /^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/;

export default function OrgGate() {
  const [slug, setSlug] = useState('');
  const [error, setError] = useState(null);

  const clean = slug.trim().toLowerCase();

  function go(e) {
    e.preventDefault();
    if (!clean) return;
    if (!SLUG_RE.test(clean)) {
      setError('Organisation names are letters, numbers and hyphens — no spaces or dots.');
      return;
    }
    if (isReservedSlug(clean)) {
      // admin.verso-cloud.io is the platform console, not a tenant.
      setError(`"${clean}" is a reserved name, not an organisation.`);
      return;
    }
    // A whole-page navigation, not a router push: the destination is a different
    // origin with its own Access session.
    window.location.href = `https://${clean}.${PRODUCT_DOMAIN}/`;
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4">
      <div className="w-full max-w-sm">
        <h1 className="text-center text-2xl font-bold text-gray-900">Verso Cloud</h1>
        <p className="mt-2 text-center text-sm text-gray-600">
          Enter your organization to sign in.
        </p>

        <form onSubmit={go} className="mt-6 rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
          <label htmlFor="org-slug" className="block text-sm font-medium text-gray-700">
            Organization
          </label>
          <div className="mt-1 flex items-stretch">
            <input
              id="org-slug"
              autoFocus
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              value={slug}
              onChange={(e) => { setSlug(e.target.value); setError(null); }}
              placeholder="your-org"
              className="min-w-0 flex-1 rounded-l-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
            <span className="inline-flex select-none items-center rounded-r-md border border-l-0 border-gray-300 bg-gray-50 px-3 text-sm text-gray-500">
              .{PRODUCT_DOMAIN}
            </span>
          </div>

          {error && <p className="mt-2 text-sm text-red-600">{error}</p>}

          <button
            type="submit"
            disabled={!clean}
            className="mt-4 w-full rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
          >
            Continue
          </button>
        </form>

        <p className="mt-4 text-center text-xs text-gray-500">
          Don’t know your organization slug? Contact your administrator.
        </p>
        <p className="mt-6 text-center text-xs text-gray-400">Colorado Communications</p>
      </div>
    </div>
  );
}
